import { useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { api, errMsg, fmtDay, onTrial } from "../lib/api";

const statusText = (loc, trial) => {
  if (loc.cancel_at_period_end) return `Canceled — stays active until ${fmtDay(loc.cancel_at)}. You won't be charged again.`;
  if (trial) return `Free trial until ${fmtDay(loc.trial_ends_at)}, then $29.99/month.`;
  return "$29.99/month · renews automatically.";
};

const CancelButton = ({ loc, trial, busy, onConfirm }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button disabled={busy} className="btn-secondary !text-red-600 !border-red-200 hover:!bg-red-50 whitespace-nowrap" data-testid={`cancel-subscription-button-${loc.slug}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : trial ? "Cancel free trial" : "Cancel subscription"}
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent className="rounded-3xl" data-testid="cancel-confirm-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display text-2xl">{trial ? "Cancel your free trial?" : "Cancel this subscription?"}</AlertDialogTitle>
        <AlertDialogDescription>
          {trial
            ? `You won't be charged. ${loc.label} keeps working until ${fmtDay(loc.trial_ends_at)}, then its survey stops accepting responses.`
            : `${loc.label} keeps working until the end of the current billing period, then its survey stops accepting responses. You won't be charged again.`}
          {" "}You can undo this any time before then.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-xl" data-testid="cancel-dialog-keep">Keep it</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="rounded-xl bg-red-600 hover:bg-red-700" data-testid="cancel-dialog-confirm">
          {trial ? "Yes, cancel trial" : "Yes, cancel"}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const SubscriptionPanel = ({ loc, onChange, compact }) => {
  const trial = onTrial(loc);
  const [busy, setBusy] = useState(false);
  const act = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(`/payments/${path}`, { location_id: loc.id });
      toast.success(msg);
      await onChange();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`subscription-panel-${loc.slug}`}>
      <div className="flex items-start gap-3 min-w-0">
        <span className="grid place-items-center w-9 h-9 shrink-0 rounded-xl bg-[#E3EDE6] text-[#2F5D4A]"><CreditCard className="w-4 h-4" /></span>
        <div className="min-w-0">
          <p className="font-semibold text-[#1A2B25]">{compact ? loc.label : "Subscription"}</p>
          <p className={`text-sm ${loc.cancel_at_period_end ? "text-red-600" : "text-[#4B5A54]"}`} data-testid={`subscription-status-text-${loc.slug}`}>{statusText(loc, trial)}</p>
        </div>
      </div>
      {!loc.stripe_subscription_id ? (
        <span className="text-xs text-[#778580]">Demo location — no billing</span>
      ) : loc.cancel_at_period_end ? (
        <button disabled={busy} onClick={() => act("resume", "Subscription resumed")} className="btn-primary whitespace-nowrap" data-testid={`resume-subscription-button-${loc.slug}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Keep subscription"}
        </button>
      ) : (
        <CancelButton loc={loc} trial={trial} busy={busy} onConfirm={() => act("cancel", trial ? "Free trial canceled — you won't be charged" : "Subscription canceled")} />
      )}
    </div>
  );
};
